import React, { useState, useEffect, useMemo } from 'react';
import { Box } from '@mui/material';
import { Map, Marker } from '@vis.gl/react-google-maps';
import { Data, parseGps, getMarkerIcon } from './utils';
import { COUNTRY_COORDINATES, CountryKey } from './constants';

interface MapContainerProps {
  data?: (Data & { markerIcon?: { url: string; scaledSize: { width: number; height: number } } })[];
  filteredData?: (Data & { markerIcon?: { url: string; scaledSize: { width: number; height: number } } })[];
  handleMarkerClick?: (box: number) => void;
  handleBtsClick?: (btsGroup: any[]) => void;
  searchedLocation?: { lat: number, lng: number } | null;
  initialZoom: number;
  btsData?: any[];
  installationLocation?: { lat: number, lng: number } | null;
  selectedCountry?: CountryKey;
  onCountryChange?: (country: CountryKey) => void;
  showSearchMarker?: boolean;
}

function parseBtsLocation(location: string): { lat: number, lng: number } {
  const latPart = location.slice(0, 7);
  const latDegrees = parseInt(latPart.slice(0, 2));
  const latMinutes = parseInt(latPart.slice(3, 5));
  const latSeconds = parseInt(latPart.slice(5, 7));
  const latFinal = latDegrees + latMinutes / 60 + latSeconds / 3600;
  const latitude = latPart[2] === 'N' ? latFinal : -latFinal;

  const lngPart = location.slice(9);
  const lngDegrees = parseInt(lngPart.slice(0, 2));
  const lngMinutes = parseInt(lngPart.slice(3, 5));
  const lngSeconds = parseInt(lngPart.slice(5, 7));
  const lngFinal = lngDegrees + lngMinutes / 60 + lngSeconds / 3600;
  const longitude = lngPart[2] === 'E' ? lngFinal : -lngFinal;

  return { lat: latitude, lng: longitude };
}

const MapContainer: React.FC<MapContainerProps> = ({
  data,
  filteredData,
  handleMarkerClick,
  handleBtsClick,
  searchedLocation,
  initialZoom,
  btsData,
  installationLocation,
  selectedCountry = 'Poland',
  onCountryChange,
  showSearchMarker = false,
}) => {
  const [cameraProps, setCameraProps] = useState({
    center: installationLocation || { lat: 52.1, lng: 19.280556 },
    zoom: installationLocation ? 12 : initialZoom,
  });

  const [lastAction, setLastAction] = useState<'search' | 'country'>('country');

  const fitBoundsToMarkers = () => {
    if (btsData && btsData.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      btsData.forEach((bts: any) => {
        const btsCoords = parseBtsLocation(bts.bts_location);
        bounds.extend(new google.maps.LatLng(btsCoords.lat, btsCoords.lng));
      });

      const center = bounds.getCenter().toJSON();
      setCameraProps({
        center,
        zoom: 12,
      });
    }
  };

  useEffect(() => {
    if (btsData && btsData.length > 0) {
      fitBoundsToMarkers();
    }
  }, [btsData]);

  const handleCameraChange = (event: any) => {
    setCameraProps({
      center: event.detail.center,
      zoom: event.detail.zoom,
    });
  };

  const getMarkerColor = (siec_id?: string) => {
    if (!siec_id) {
      return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
    }

    switch (siec_id.toLowerCase()) {
      case 'orange':
        return 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png';
      case 'play':
        return 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png';
      case 'plus':
        return 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
      default:
        return 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';
    }
  };

  const groupBtsByLocation = (btsData: any[]) => {
    const groupedData: { [key: string]: any[] } = {};

    btsData.forEach((bts) => {
      const btsCoords = parseBtsLocation(bts.bts_location);
      const coordsKey = `${btsCoords.lat},${btsCoords.lng}`;

      if (!groupedData[coordsKey]) {
        groupedData[coordsKey] = [];
      }

      groupedData[coordsKey].push(bts);
    });

    return groupedData;
  };

  const groupedBtsData = groupBtsByLocation(btsData || []);

  const renderBtsMarkers = () => {
    if (!window.google?.maps) return null;

    const seenCoords: { [key: string]: number } = {};

    return btsData?.map((bts, index) => {
      const btsCoords = parseBtsLocation(bts.bts_location);
      const coordsKey = `${btsCoords.lat},${btsCoords.lng}`;
      const markerIcon = getMarkerColor(bts.siec_id);


      let markerSizeMultiplier = 1;
      let zIndex = 1;

      if (seenCoords[coordsKey]) {
        if (seenCoords[coordsKey] === 1) {
          markerSizeMultiplier = 1.3;
          zIndex = 0;
        } else if (seenCoords[coordsKey] === 2) {
          markerSizeMultiplier = 0.8;
          zIndex = 2;
        }
      }

      seenCoords[coordsKey] = (seenCoords[coordsKey] || 0) + 1;

      return (
        <Marker
          key={`bts-marker-${index}`}
          position={btsCoords}
          icon={{
            url: markerIcon,
            scaledSize: new google.maps.Size(30 * markerSizeMultiplier, 32 * markerSizeMultiplier),
          }}
          zIndex={zIndex}
          onClick={() => handleBtsClick && handleBtsClick(groupedBtsData[coordsKey])}
        />
      );
    });
  };

  useEffect(() => {
    console.log('Effect triggered:', { searchedLocation, selectedCountry, initialZoom, lastAction });
    
    if (searchedLocation && lastAction === 'search') {
      console.log('Setting camera to searched location');
      setCameraProps({
        center: searchedLocation,
        zoom: initialZoom,
      });
    } else if (selectedCountry && COUNTRY_COORDINATES[selectedCountry]) {
      console.log('Setting camera to country coordinates');
      setCameraProps({
        center: COUNTRY_COORDINATES[selectedCountry].center,
        zoom: COUNTRY_COORDINATES[selectedCountry].zoom || initialZoom,
      });
    }
  }, [searchedLocation, selectedCountry, initialZoom, lastAction]);

  useEffect(() => {
    if (searchedLocation) {
      setLastAction('search');
    }
  }, [searchedLocation]);

  useEffect(() => {
    if (selectedCountry) {
      setLastAction('country');
    }
  }, [selectedCountry]);

  const getMarkerZIndex = (color: string, box: number): number => {
    const baseZIndex = {
      'red': 3000000,
      'yellow': 2000000,
      'green': 1000000,
      'unknown': 0
    }[color] || 0;
    
    return baseZIndex + (box % 1000); // Use box number for sub-ordering within color groups
  };

  const memoizedMarkers = useMemo(() => {
    if (!window.google?.maps) return null;

    return filteredData?.map((d, index) => {
      const { lat, lng } = parseGps(d.gps);
      let markerIcon;
      
      if (d.markerIcon) {
        markerIcon = d.markerIcon;
      } else {
        markerIcon = getMarkerIcon(d.pm10, d.pm25, lat, lng);
      }

      const zIndex = getMarkerZIndex(markerIcon.color || '', d.box);

      return (
        <Marker
          key={`marker-${d.box}-${index}`}
          position={{ lat, lng }}
          icon={{
            url: markerIcon.url,
            scaledSize: new google.maps.Size(markerIcon.scaledSize.width, markerIcon.scaledSize.height),
          }}
          onClick={() => handleMarkerClick && handleMarkerClick(d.box)}
          zIndex={zIndex}
        />
      );
    });
  }, [filteredData, handleMarkerClick]);

  return (
    <Box sx={{ height: '100%', flex: '1 1 auto', border: '1px solid', borderColor: 'borders.primary', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Map
        center={cameraProps.center}
        zoom={cameraProps.zoom}
        onCameraChanged={handleCameraChange}
      >
        {installationLocation && (
          <Marker position={installationLocation} />
        )}
        {btsData && renderBtsMarkers()}
        {memoizedMarkers}
        {showSearchMarker && searchedLocation && (
          <Marker
            position={searchedLocation}
            icon={{
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new google.maps.Size(50, 50),
            }}
            zIndex={1000}
          />
        )}
      </Map>
    </Box>
  );
};

export default MapContainer;
